import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarCheck } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { TextField } from '@/components/forms/Field';
import { Stat } from '@/features/library/shared';
import { portalApi } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { formatDate, humanise } from '@/utils/format';
import { PortalShell, type PortalContext } from './PortalShell';
import { AttendanceBadge, SectionTitle, localDaysAgo, localToday, percentText } from './shared';

/**
 * Attendance for one student over a chosen range.
 *
 * The range defaults to the last 30 days, the same window the server uses when
 * none is given. Every count and the percentage are the server's; the server
 * also refuses a range longer than 366 days, and says so.
 */
export function PortalAttendancePage() {
  return (
    <PortalShell title="Attendance" icon={<CalendarCheck className="h-5 w-5" aria-hidden="true" />}>
      {(context) => <Attendance context={context} />}
    </PortalShell>
  );
}

function Attendance({ context }: { context: PortalContext }) {
  const { subject, subjectKey } = context;
  const [from, setFrom] = useState(localDaysAgo(29));
  const [to, setTo] = useState(localToday());

  const rangeValid = from !== '' && to !== '' && from <= to;

  const attendance = useQuery({
    queryKey: queryKeys.portal.attendance(subjectKey, from, to),
    queryFn: () => portalApi.attendance(subject, { from, to }),
    enabled: rangeValid,
    retry: false,
  });

  const summary = attendance.data?.summary;

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="grid gap-3 sm:grid-cols-[repeat(2,minmax(0,12rem))]">
          <TextField
            id="attendance-from"
            label="From"
            type="date"
            value={from}
            max={to}
            onChange={(event) => setFrom(event.target.value)}
          />
          <TextField
            id="attendance-to"
            label="To"
            type="date"
            value={to}
            min={from}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
        {!rangeValid && (
          <p className="mt-2 text-xs text-danger">Choose a start date on or before the end date.</p>
        )}
      </Card>

      {!rangeValid ? null : attendance.isPending ? (
        <Card>
          <LoadingState label="Loading attendance" />
        </Card>
      ) : attendance.isError ? (
        <Card>
          <ErrorState error={attendance.error} onRetry={() => void attendance.refetch()} />
        </Card>
      ) : (
        <>
          {summary !== undefined && (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat
                label="Attendance"
                value={percentText(summary.attendancePercentage)}
                hint={`${summary.recordedDays} day${summary.recordedDays === 1 ? '' : 's'} recorded`}
                tone="success"
              />
              <Stat label="Present" value={summary.presentDays} />
              <Stat
                label="Absent"
                value={summary.absentDays}
                tone={summary.absentDays > 0 ? 'danger' : 'neutral'}
              />
              <Stat
                label="Late"
                value={summary.lateDays}
                tone={summary.lateDays > 0 ? 'warning' : 'neutral'}
                hint={`${summary.excusedDays} excused · ${summary.leaveDays} on leave`}
              />
            </div>
          )}

          <Card>
            <SectionTitle>
              {formatDate(attendance.data.from)} – {formatDate(attendance.data.to)}
            </SectionTitle>
            {attendance.data.records.length === 0 ? (
              <EmptyState
                icon={<CalendarCheck className="h-6 w-6" aria-hidden="true" />}
                title="Nothing recorded"
                description="No attendance has been recorded in this range."
              />
            ) : (
              <ul className="divide-y divide-line">
                {attendance.data.records.map((record) => (
                  <li key={record.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-ink">
                        {formatDate(record.attendanceDate)}
                      </span>
                      <span className="block text-[11px] text-subtle">
                        {humanise(record.attendanceType)}
                        {record.remarks !== undefined && ` · ${record.remarks}`}
                      </span>
                    </span>
                    <AttendanceBadge status={record.status} />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
